import ReactDOM from "react-dom/client";
import App from "./App";
import { UserProvider } from "./UserContext";
import { BrandingProvider } from "./BrandingContext";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { BrowserRouter } from "react-router-dom";
// import { AuthProvider } from "./context/AuthProvider";

import { ApolloClient, createHttpLink, InMemoryCache, ApolloProvider } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";

import { Provider } from "react-redux";
import store from "./utils/redux/store";

import { SnackbarProvider } from 'notistack';

import axios from "axios";

import * as Sentry from "@sentry/react";

import { CssBaseline } from "@mui/material";
import { requestSessionExpiredLogout } from "./utils/sessionExpiredLogout";

const AXIOS_401_EXEMPT_PREFIXES = [
  "/api/auth/login",
  "/api/auth/mfa/",
  "/api/onboarding/",
  "/api/upload-link/",
];

function isAxios401Exempt(url = "") {
  try {
    const path = url.startsWith("http") ? new URL(url).pathname : url.split("?")[0];
    return AXIOS_401_EXEMPT_PREFIXES.some(
      (prefix) => path === prefix.replace(/\/$/, "") || path.startsWith(prefix)
    );
  } catch {
    return false;
  }
}

if (process.env.NODE_ENV !== "development") {
  console.log = () => {};
  console.error = () => {};
  console.debug = () => {};
  console.info = () => {};

  Sentry.init({
    environment: process.env.NODE_ENV,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      // Sentry.feedbackIntegration({
      //   // Additional SDK configuration goes in here, for example:
      //   colorScheme: "light",
      // }),
    ],
    // Performance Monitoring
    tracesSampleRate: 0.1, //  Capture 10% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["uicharitable.org", /^https:\/\/(?:.*\.)?uicharitable\.org/], // /^https:\/\/yourserver\.io\/api/
    // Session Replay
    replaysSessionSampleRate: 0.5, // This sets the sample rate at 50%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));

const httpLink = createHttpLink({
  uri: "/graphql",
  credentials: "include",
});

// pass auth token from local storage to request headers for graphql and axios
// axios.defaults.headers.common["authorization"] = "Bearer " + localStorage.getItem("idToken"); // OLD
axios.defaults.withCredentials = true;
axios.interceptors.request.use(function (config) {
  // Do not attach Authorization header; rely on HttpOnly cookies
  return config;
});
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !isAxios401Exempt(error.config?.url || "")) {
      requestSessionExpiredLogout();
    }
    return Promise.reject(error);
  }
);

const authLink = setContext(async (_, { headers }) => {
  // With cookie-based auth, do not set Authorization header
  return { headers };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  const unauthenticated = graphQLErrors?.some(
    (err) => err.extensions?.code === "UNAUTHENTICATED"
  );
  const network401 = networkError?.statusCode === 401 || networkError?.status === 401;
  if (unauthenticated || network401) {
    requestSessionExpiredLogout();
  }
});

// When initializing the ApolloClient we add a typePolicy rule to InternalUser.meta because the type UserMeta has no
// unique identifier, such as _id, for caching a particular instance. See https://www.apollographql.com/docs/react/caching/cache-field-behavior/#merging-non-normalized-objects
const client = new ApolloClient({
  link: authLink.concat(errorLink).concat(httpLink),
  cache: new InMemoryCache({
    typePolicies: {
      InternalUser: {
        fields: {
          meta: {
            merge: false,
          },
        },
      },
      // Disable normalization for TableColumn - these objects share the same 'id' values
      // across different teams (e.g., "key", "summary", "status") but should NOT share
      // cache entries. Without this, Team 1's column visibility bleeds into Team 2's.
      TableColumn: {
        keyFields: false,
      },
      // totals is an embedded object with no id. Default replace drops fields
      // when a query asks for a subset (e.g. fee cash/stock only).
      DafProductTotals: {
        merge: true,
      },
      ClatProductTotals: {
        merge: true,
      },
    },
  }),
});

root.render(
  <ApolloProvider client={client}>
    <Provider store={store}>
      <BrowserRouter>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {/* <AuthProvider> */}
          <BrandingProvider>
            <UserProvider>
              <CssBaseline />
              <SnackbarProvider maxSnack={5}>
                <App />
              </SnackbarProvider>
            </UserProvider>
          </BrandingProvider>
          {/* </AuthProvider> */}
        </LocalizationProvider>
      </BrowserRouter>
    </Provider>
  </ApolloProvider>
);
